import React, { useState, useEffect, useCallback } from 'react';
import { suppliersApi, purchaseOrdersApi, inventoryApi, supplyChainApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Truck, Plus, Package, ShoppingCart, Warning, Trash, Factory, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TIER = {
  preferred: { label: 'Preferred', color: 'bg-purple-500/10 text-purple-700 border-purple-500/30' },
  approved:  { label: 'Approved',  color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' },
  trial:     { label: 'Trial',     color: 'bg-amber-500/10 text-amber-700 border-amber-500/30' },
  blocked:   { label: 'Blocked',   color: 'bg-destructive/10 text-destructive border-destructive/30' },
};
const PO_STATUS = {
  draft:        { label: 'Draft',        color: 'bg-zinc-500/10 text-zinc-700' },
  sent:         { label: 'Sent',         color: 'bg-blue-500/10 text-blue-700' },
  acknowledged: { label: 'Acknowledged', color: 'bg-indigo-500/10 text-indigo-700' },
  received:     { label: 'Received',     color: 'bg-emerald-500/10 text-emerald-700' },
  completed:    { label: 'Completed',    color: 'bg-teal-500/10 text-teal-700' },
  cancelled:    { label: 'Cancelled',    color: 'bg-destructive/10 text-destructive' },
};

export default function SupplyChainPage() {
  const [tab, setTab] = useState('suppliers');
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'supplier'|'po'|'inventory'
  const [sForm, setSForm] = useState({ name: '', contact_name: '', contact_email: '', contact_phone: '', country: '', category: '', tier: 'approved' });
  const [poForm, setPoForm] = useState({ supplier_id: '', status: 'draft', currency: 'USD', items: [{ _key: `it_${Date.now()}`, name: '', quantity: 1, unit_price: 0 }], expected_delivery: '', notes: '' });
  const [iForm, setIForm] = useState({ sku: '', name: '', category: '', unit: 'unit', quantity: 0, reorder_level: 10, unit_cost: 0, location: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [s, p, i, st] = await Promise.all([
        suppliersApi.getAll({ limit: 200 }),
        purchaseOrdersApi.getAll({ limit: 100 }),
        inventoryApi.getAll({ limit: 200 }),
        supplyChainApi.stats(),
      ]);
      setSuppliers(s.data);
      setPos(p.data);
      setInventory(i.data);
      setStats(st.data);
    } catch { toast.error('Failed to load supply chain data'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createSupplier = async (e) => {
    e.preventDefault();
    try {
      await suppliersApi.create(sForm);
      toast.success('Supplier added');
      setDialog(null);
      setSForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', country: '', category: '', tier: 'approved' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createPO = async (e) => {
    e.preventDefault();
    try {
      const cleanItems = poForm.items.filter(i => i.name?.trim()).map(i => ({ name: i.name, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 }));
      await purchaseOrdersApi.create({ ...poForm, items: cleanItems });
      toast.success('Purchase order created');
      setDialog(null);
      setPoForm({ supplier_id: '', status: 'draft', currency: 'USD', items: [{ _key: `it_${Date.now()}`, name: '', quantity: 1, unit_price: 0 }], expected_delivery: '', notes: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createInventory = async (e) => {
    e.preventDefault();
    try {
      await inventoryApi.create({ ...iForm, quantity: Number(iForm.quantity), reorder_level: Number(iForm.reorder_level), unit_cost: Number(iForm.unit_cost) });
      toast.success('Item added');
      setDialog(null);
      setIForm({ sku: '', name: '', category: '', unit: 'unit', quantity: 0, reorder_level: 10, unit_cost: 0, location: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const del = async (type, id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      if (type === 'supplier') await suppliersApi.delete(id);
      if (type === 'po') await purchaseOrdersApi.delete(id);
      if (type === 'inventory') await inventoryApi.delete(id);
      toast.success('Deleted');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addPoItem = () => setPoForm({ ...poForm, items: [...poForm.items, { _key: `it_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: '', quantity: 1, unit_price: 0 }] });

  return (
    <div className="space-y-6" data-testid="supply-chain-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Supply Chain</h1>
        <p className="text-muted-foreground text-sm mt-1">Suppliers · Purchase Orders · Inventory</p>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Kpi icon={Factory}      label="Suppliers"       value={stats.suppliers} />
          <Kpi icon={ShoppingCart} label="Purchase Orders" value={stats.purchase_orders} />
          <Kpi icon={ShoppingCart} label="Pending POs"     value={stats.pending_purchase_orders} color="text-amber-500" />
          <Kpi icon={Package}      label="SKUs"            value={stats.inventory_items} />
          <Kpi icon={Warning}      label="Low Stock"       value={stats.low_stock_items} color="text-destructive" />
          <Kpi icon={ChartBar}     label="PO Value"        value={`$${Math.round(stats.total_po_value || 0).toLocaleString()}`} color="text-emerald-500" />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border flex gap-6">
        <TabBtn active={tab === 'suppliers'} onClick={() => setTab('suppliers')} icon={Factory}      label="Suppliers"       count={suppliers.length} />
        <TabBtn active={tab === 'pos'}       onClick={() => setTab('pos')}       icon={ShoppingCart} label="Purchase Orders" count={pos.length} />
        <TabBtn active={tab === 'inventory'} onClick={() => setTab('inventory')} icon={Package}      label="Inventory"       count={inventory.length} />
      </div>

      {/* Add button */}
      <div className="flex justify-end">
        <Button onClick={() => setDialog(tab === 'suppliers' ? 'supplier' : tab === 'pos' ? 'po' : 'inventory')} data-testid={`new-${tab}-btn`}>
          <Plus size={16} className="mr-1" /> New {tab === 'suppliers' ? 'Supplier' : tab === 'pos' ? 'Purchase Order' : 'Item'}
        </Button>
      </div>

      {/* Content */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? <p className="p-12 text-center text-muted-foreground">Loading…</p> :
         tab === 'suppliers' ? <SuppliersTable data={suppliers} onDelete={(id) => del('supplier', id)} /> :
         tab === 'pos' ? <POsTable data={pos} onDelete={(id) => del('po', id)} /> :
         <InventoryTable data={inventory} onDelete={(id) => del('inventory', id)} />}
      </div>

      {/* Supplier Dialog */}
      <Dialog open={dialog === 'supplier'} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Supplier</DialogTitle></DialogHeader>
          <form onSubmit={createSupplier} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name *</Label><Input value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} required /></div>
              <div><Label>Contact name</Label><Input value={sForm.contact_name} onChange={(e) => setSForm({ ...sForm, contact_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={sForm.contact_email} onChange={(e) => setSForm({ ...sForm, contact_email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={sForm.contact_phone} onChange={(e) => setSForm({ ...sForm, contact_phone: e.target.value })} /></div>
              <div><Label>Country</Label><Input value={sForm.country} onChange={(e) => setSForm({ ...sForm, country: e.target.value })} /></div>
              <div><Label>Category</Label><Input value={sForm.category} onChange={(e) => setSForm({ ...sForm, category: e.target.value })} placeholder="e.g. Raw Materials" /></div>
              <div>
                <Label>Tier</Label>
                <Select value={sForm.tier} onValueChange={(v) => setSForm({ ...sForm, tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TIER).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancel</Button><Button type="submit">Add</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PO Dialog */}
      <Dialog open={dialog === 'po'} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
          <form onSubmit={createPO} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Supplier *</Label>
                <Select value={poForm.supplier_id} onValueChange={(v) => setPoForm({ ...poForm, supplier_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Expected delivery</Label><Input type="date" value={poForm.expected_delivery} onChange={(e) => setPoForm({ ...poForm, expected_delivery: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Line Items</Label><Button type="button" variant="ghost" size="sm" onClick={addPoItem}><Plus size={14} className="mr-1" />Add</Button></div>
              {poForm.items.map((it, idx) => (
                <div key={it._key || idx} className="grid grid-cols-12 gap-2 mb-2">
                  <Input className="col-span-6" placeholder="Item name" value={it.name} onChange={(e) => { const n = [...poForm.items]; n[idx] = { ...it, name: e.target.value }; setPoForm({ ...poForm, items: n }); }} />
                  <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => { const n = [...poForm.items]; n[idx] = { ...it, quantity: e.target.value }; setPoForm({ ...poForm, items: n }); }} />
                  <Input className="col-span-3" type="number" placeholder="Unit price" value={it.unit_price} onChange={(e) => { const n = [...poForm.items]; n[idx] = { ...it, unit_price: e.target.value }; setPoForm({ ...poForm, items: n }); }} />
                  <Button type="button" variant="ghost" size="sm" className="col-span-1 text-destructive" onClick={() => setPoForm({ ...poForm, items: poForm.items.filter((_, i) => i !== idx) })}><Trash size={14} /></Button>
                </div>
              ))}
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={poForm.notes} onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancel</Button><Button type="submit">Create PO</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inventory Dialog */}
      <Dialog open={dialog === 'inventory'} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Inventory Item</DialogTitle></DialogHeader>
          <form onSubmit={createInventory} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU *</Label><Input value={iForm.sku} onChange={(e) => setIForm({ ...iForm, sku: e.target.value })} required /></div>
              <div><Label>Name *</Label><Input value={iForm.name} onChange={(e) => setIForm({ ...iForm, name: e.target.value })} required /></div>
              <div><Label>Category</Label><Input value={iForm.category} onChange={(e) => setIForm({ ...iForm, category: e.target.value })} /></div>
              <div><Label>Unit</Label><Input value={iForm.unit} onChange={(e) => setIForm({ ...iForm, unit: e.target.value })} /></div>
              <div><Label>Quantity</Label><Input type="number" value={iForm.quantity} onChange={(e) => setIForm({ ...iForm, quantity: e.target.value })} /></div>
              <div><Label>Reorder level</Label><Input type="number" value={iForm.reorder_level} onChange={(e) => setIForm({ ...iForm, reorder_level: e.target.value })} /></div>
              <div><Label>Unit cost</Label><Input type="number" value={iForm.unit_cost} onChange={(e) => setIForm({ ...iForm, unit_cost: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={iForm.location} onChange={(e) => setIForm({ ...iForm, location: e.target.value })} /></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancel</Button><Button type="submit">Add</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count }) {
  return (
    <button onClick={onClick} className={`relative flex items-center gap-2 py-3 text-sm font-medium transition-colors ${active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
      <Icon size={16} weight={active ? 'fill' : 'regular'} /><span>{label}</span>
      {typeof count === 'number' && <span className={`text-xs rounded-full min-w-5 h-5 px-1.5 inline-flex items-center justify-center ${active ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{count}</span>}
      {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
    </button>
  );
}

function Kpi({ icon: Icon, label, value, color = 'text-primary' }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center ${color}`}><Icon size={20} weight="duotone" /></div>
        <div><p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p><p className="text-lg font-bold mt-1">{value}</p></div>
      </div>
    </div>
  );
}

function SuppliersTable({ data, onDelete }) {
  if (data.length === 0) return <EmptyT label="suppliers" />;
  return (
    <table className="w-full">
      <thead><tr className="border-b border-border bg-muted/50"><Th>Name</Th><Th>Contact</Th><Th>Category</Th><Th>Country</Th><Th>Tier</Th><Th className="text-right">Actions</Th></tr></thead>
      <tbody>
        {data.map(s => (
          <tr key={s.id} className="border-b border-border hover:bg-muted/30" data-testid={`sup-${s.id}`}>
            <Td><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.category || '—'}</p></Td>
            <Td><p className="text-sm">{s.contact_name || '—'}</p><p className="text-xs text-muted-foreground">{s.contact_email || ''}</p></Td>
            <Td className="text-sm">{s.category || '—'}</Td>
            <Td className="text-sm">{s.country || '—'}</Td>
            <Td><Badge variant="outline" className={TIER[s.tier]?.color}>{TIER[s.tier]?.label || s.tier}</Badge></Td>
            <Td className="text-right"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(s.id)}><Trash size={14} /></Button></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function POsTable({ data, onDelete }) {
  if (data.length === 0) return <EmptyT label="purchase orders" />;
  return (
    <table className="w-full">
      <thead><tr className="border-b border-border bg-muted/50"><Th>PO Number</Th><Th>Supplier</Th><Th>Status</Th><Th className="text-right">Total</Th><Th>Expected</Th><Th className="text-right">Actions</Th></tr></thead>
      <tbody>
        {data.map(p => (
          <tr key={p.id} className="border-b border-border hover:bg-muted/30" data-testid={`po-${p.id}`}>
            <Td className="font-mono text-sm font-medium">{p.po_number}</Td>
            <Td className="text-sm">{p.supplier_name || '—'}</Td>
            <Td><Badge variant="outline" className={PO_STATUS[p.status]?.color}>{PO_STATUS[p.status]?.label || p.status}</Badge></Td>
            <Td className="text-right font-semibold">{p.currency || 'USD'} {(p.total || 0).toLocaleString()}</Td>
            <Td className="text-sm text-muted-foreground">{p.expected_delivery ? formatDate(p.expected_delivery) : '—'}</Td>
            <Td className="text-right"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(p.id)}><Trash size={14} /></Button></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InventoryTable({ data, onDelete }) {
  if (data.length === 0) return <EmptyT label="inventory items" />;
  return (
    <table className="w-full">
      <thead><tr className="border-b border-border bg-muted/50"><Th>SKU</Th><Th>Name</Th><Th>Category</Th><Th className="text-right">Qty</Th><Th className="text-right">Reorder</Th><Th className="text-right">Unit Cost</Th><Th>Location</Th><Th className="text-right">Actions</Th></tr></thead>
      <tbody>
        {data.map(it => {
          const low = (it.quantity || 0) <= (it.reorder_level || 0);
          return (
            <tr key={it.id} className="border-b border-border hover:bg-muted/30" data-testid={`inv-${it.id}`}>
              <Td className="font-mono text-sm">{it.sku}</Td>
              <Td className="font-medium">{it.name}</Td>
              <Td className="text-sm">{it.category || '—'}</Td>
              <Td className={`text-right font-semibold ${low ? 'text-destructive' : ''}`}>{it.quantity} {it.unit}{low && <Warning size={12} className="inline ml-1" weight="fill" />}</Td>
              <Td className="text-right text-sm text-muted-foreground">{it.reorder_level}</Td>
              <Td className="text-right text-sm">${(it.unit_cost || 0).toFixed(2)}</Td>
              <Td className="text-sm text-muted-foreground">{it.location || '—'}</Td>
              <Td className="text-right"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(it.id)}><Trash size={14} /></Button></Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const Th = ({ children, className = '' }) => <th className={`text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`p-3 ${className}`}>{children}</td>;
const EmptyT = ({ label }) => (
  <div className="text-center p-12">
    <Truck size={40} className="mx-auto text-muted-foreground opacity-40 mb-2" />
    <p className="text-sm text-muted-foreground">No {label} yet</p>
  </div>
);
